/**
 * UpdatesSettings - Settings page component for OTA updates and full app updates
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
  VStack,
  HStack,
  Box,
  Text,
  Button,
  Icon,
  Badge,
  Divider,
  Progress,
} from 'native-base';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useUpdates } from '../../hooks/useUpdates';
import { UpdateModal } from '../modal/UpdateModal';
import { getText } from 'src/utils/i18n';

interface AppUpdateInfo {
  version?: string;
  releaseDate?: string;
  releaseNotes?: string;
}

interface AppUpdateStatus {
  status: 'idle' | 'checking' | 'available' | 'not-available' | 'downloading' | 'downloaded' | 'installing' | 'error';
  info?: AppUpdateInfo;
  progress?: { percent: number; transferred: number; total: number };
  error?: string;
}

const APP_UPDATE_LOG_PREFIX = '[App Update UI]';
const CUDA_AUTO_DOWNLOAD_ATTEMPTED_VERSION_KEY = '@cuda_auto_download_attempted_version';

export const UpdatesSettings: React.FC = () => {
  const {
    currentVersion,
    availableUpdates,
    downloadProgress,
    hasUpdates,
    hasReadyUpdates,
    isDownloading,
    mandatoryUpdatesCount,
    checkForUpdates,
    forceCheckForUpdates,
    downloadUpdate,
    applyUpdates,
    showUpdateModal,
    openUpdateModal,
    closeUpdateModal,
    updateStatus,
  } = useUpdates();

  // Full app update state
  const [appUpdateStatus, setAppUpdateStatus] = useState<AppUpdateStatus>({ status: 'idle' });
  const [appUpdateInfo, setAppUpdateInfo] = useState<AppUpdateInfo | null>(null);

  // Check if running in Electron
  const isElectron = typeof window !== 'undefined' && (window as any).electronAPI;
  const electronPlatform = typeof window !== 'undefined' ? (window as any)?.electron?.platform : null;
  const isWindowsElectron = !!isElectron && electronPlatform === 'win32';

  const cudaUpdate = availableUpdates.find((u) => u.type === 'ml_runtime_cuda');
  const cudaProgress: any = (downloadProgress as any)?.ml_runtime_cuda;

  const [cudaCompat, setCudaCompat] = useState<{ compatible: boolean; gpus: string[]; reason?: string; error?: string } | null>(null);
  const [cudaInstalled, setCudaInstalled] = useState<{ installed: boolean; version?: string | null } | null>(null);
  const [cpuInstalled, setCpuInstalled] = useState<{ installed: boolean; version?: string | null } | null>(null);

  useEffect(() => {
    if (!isWindowsElectron) return;
    const electronAPI = (window as any).electronAPI;
    let cancelled = false;
    (async () => {
      try {
        const compat = await electronAPI.checkCudaCompatibility?.();
        if (!cancelled && compat) {
          setCudaCompat({
            compatible: !!compat.compatible,
            gpus: Array.isArray(compat.gpus) ? compat.gpus : [],
            reason: compat.reason,
            error: compat.error,
          });
        }
      } catch (err: any) {
        if (!cancelled) setCudaCompat({ compatible: false, gpus: [], error: err?.message || 'Unable to detect GPU' });
      }

      try {
        const status = await electronAPI.getMlRuntimeStatus?.();
        if (!cancelled && status?.success) {
          setCudaInstalled({ installed: !!status.cudaInstalled, version: status.cudaVersion ?? null });
          setCpuInstalled({ installed: !!status.cpuInstalled, version: status.cpuVersion ?? null });
        }
      } catch {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isWindowsElectron, cudaUpdate?.version, cudaProgress?.status, updateStatus]);

  const handleCudaDownload = useCallback(async () => {
    if (!cudaUpdate) return;
    downloadUpdate(cudaUpdate);
  }, [cudaUpdate, downloadUpdate]);

  const handleCudaRetry = useCallback(async () => {
    if (!cudaUpdate) return;
    try {
      const attempted = await AsyncStorage.getItem(CUDA_AUTO_DOWNLOAD_ATTEMPTED_VERSION_KEY);
      if (String(attempted || '').trim() === String(cudaUpdate.version || '').trim()) {
        await AsyncStorage.removeItem(CUDA_AUTO_DOWNLOAD_ATTEMPTED_VERSION_KEY);
      }
    } catch {
      // ignore
    }
    downloadUpdate(cudaUpdate);
  }, [cudaUpdate, downloadUpdate]);

  const handleCudaApply = useCallback(() => {
    applyUpdates();
  }, [applyUpdates]);

  const handleCpuRepair = useCallback(async () => {
    if (!isWindowsElectron) return;
    const electronAPI = (window as any).electronAPI;
    if (!electronAPI?.ensureMlRuntimeInstalled) return;
    try {
      const resp = await electronAPI.ensureMlRuntimeInstalled({ kind: 'cpu', source: 'bundled' });
      if (resp && resp.success === false) {
        console.warn('[ML Runtime] CPU runtime repair failed:', resp.error || 'Unknown error');
        return;
      }
      const status = await electronAPI.getMlRuntimeStatus?.();
      if (status?.success) {
        setCpuInstalled({ installed: !!status.cpuInstalled, version: status.cpuVersion ?? null });
      }
    } catch (error: any) {
      console.warn('[ML Runtime] CPU runtime repair failed:', error?.message || error);
    }
  }, [isWindowsElectron]);

  // Set up listener for app update status
  useEffect(() => {
    if (!isElectron) return;

    const electronAPI = (window as any).electronAPI;
    if (electronAPI?.onAppUpdateStatus) {
      console.log(`${APP_UPDATE_LOG_PREFIX} Setting up app update status listener`);
      const unsubscribe = electronAPI.onAppUpdateStatus((data: any) => {
        console.log(`${APP_UPDATE_LOG_PREFIX} Received status:`, data);
        setAppUpdateStatus(data);
        if (data.info) {
          setAppUpdateInfo(data.info);
        }
      });

      // Get initial status
      electronAPI.getAppUpdateStatus?.().then((status: any) => {
        console.log(`${APP_UPDATE_LOG_PREFIX} Initial status:`, status);
        if (status.updateAvailable && status.updateInfo) {
          setAppUpdateStatus({ status: 'available', info: status.updateInfo });
          setAppUpdateInfo(status.updateInfo);
        } else if (status.updateDownloaded) {
          setAppUpdateStatus({ status: 'downloaded', info: status.updateInfo });
          setAppUpdateInfo(status.updateInfo);
        }
      });

      return unsubscribe;
    }
  }, [isElectron]);

  const handleCheckAppUpdate = useCallback(async () => {
    if (!isElectron) return;

    const electronAPI = (window as any).electronAPI;
    console.log(`${APP_UPDATE_LOG_PREFIX} Checking for app update...`);
    setAppUpdateStatus({ status: 'checking' });

    try {
      const result = await electronAPI.checkForAppUpdate();
      console.log(`${APP_UPDATE_LOG_PREFIX} Check result:`, result);
      if (result.error) {
        setAppUpdateStatus({ status: 'error', error: result.error });
      } else if (result.updateAvailable) {
        setAppUpdateStatus({ status: 'available', info: result.updateInfo });
        setAppUpdateInfo(result.updateInfo);
      } else {
        setAppUpdateStatus({ status: 'not-available' });
      }
    } catch (error: any) {
      console.error(`${APP_UPDATE_LOG_PREFIX} Check failed:`, error);
      setAppUpdateStatus({ status: 'error', error: error.message });
    }
  }, [isElectron]);

  const handleDownloadAppUpdate = useCallback(async () => {
    if (!isElectron) return;

    const electronAPI = (window as any).electronAPI;
    console.log(`${APP_UPDATE_LOG_PREFIX} Downloading app update...`);
    setAppUpdateStatus({ status: 'downloading', progress: { percent: 0, transferred: 0, total: 0 } });

    try {
      const result = await electronAPI.downloadAppUpdate();
      console.log(`${APP_UPDATE_LOG_PREFIX} Download result:`, result);
      if (result.error) {
        setAppUpdateStatus({ status: 'error', error: result.error });
      }
      // Success will be handled by the status listener
    } catch (error: any) {
      console.error(`${APP_UPDATE_LOG_PREFIX} Download failed:`, error);
      setAppUpdateStatus({ status: 'error', error: error.message });
    }
  }, [isElectron]);

  const handleInstallAppUpdate = useCallback(async () => {
    if (!isElectron) return;

    const electronAPI = (window as any).electronAPI;
    console.log(`${APP_UPDATE_LOG_PREFIX} Installing app update...`);
    setAppUpdateStatus({ status: 'installing', info: appUpdateInfo || undefined });

    try {
      const result = await electronAPI.installAppUpdate();
      if (result?.error) {
        setAppUpdateStatus({ status: 'error', error: result.error });
      }
    } catch (error: any) {
      console.error(`${APP_UPDATE_LOG_PREFIX} Install failed:`, error);
      setAppUpdateStatus({ status: 'error', error: error.message });
    }
  }, [isElectron, appUpdateInfo]);

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  return (
    <Box p={4}>
      <VStack space={4}>
        {/* Header */}
        <HStack justifyContent="space-between" alignItems="center">
          <Text fontSize="xl" fontWeight="bold">
            {getText('appUpdates')}
          </Text>
          {(hasUpdates || appUpdateStatus.status === 'available') && (
            <Badge colorScheme="blue" variant="solid">
              {getText('updatesAvailable')}
            </Badge>
          )}
        </HStack>

        <Divider />

        {/* Current Version */}
        <Box p={4} bg="coolGray.50" borderRadius="md">
          <VStack space={2}>
            <HStack justifyContent="space-between">
              <Text fontWeight="bold">{getText('currentVersion')}</Text>
              <Text>{currentVersion}</Text>
            </HStack>
          </VStack>
        </Box>

        {/* Full App Update Section (Electron only) */}
        {isElectron && (
          <>
            <Divider />
            <Text fontSize="lg" fontWeight="semibold">
              {getText('fullAppUpdate')}
            </Text>

            {/* App Update Available */}
            {appUpdateStatus.status === 'available' && appUpdateInfo && (
              <Box
                p={4}
                bg="green.50"
                borderRadius="md"
                borderWidth={1}
                borderColor="green.200"
              >
                <VStack space={2}>
                  <HStack space={2} alignItems="center">
                    <Icon
                      as={MaterialCommunityIcons}
                      name="download-circle"
                      size="md"
                      color="green.600"
                    />
                    <Text fontWeight="bold" color="green.700">
                      {getText('newVersionAvailable')}: {appUpdateInfo.version}
                    </Text>
                  </HStack>

                  <Text fontSize="sm" color="coolGray.600">
                    {getText('newVersionAvailableDescription')}
                  </Text>

                  <Button
                    mt={2}
                    onPress={handleDownloadAppUpdate}
                    colorScheme="green"
                    leftIcon={<Icon as={MaterialCommunityIcons} name="download" />}
                  >
                    {getText('downloadUpdate')}
                  </Button>
                </VStack>
              </Box>
            )}

            {/* App Update Downloading */}
            {appUpdateStatus.status === 'downloading' && (
              <Box
                p={4}
                bg="blue.50"
                borderRadius="md"
                borderWidth={1}
                borderColor="blue.200"
              >
                <VStack space={2}>
                  <HStack space={2} alignItems="center">
                    <Icon
                      as={MaterialCommunityIcons}
                      name="download"
                      size="md"
                      color="blue.600"
                    />
                    <Text fontWeight="bold" color="blue.700">
                      {getText('downloadingUpdate')}
                    </Text>
                  </HStack>

                  <Progress
                    value={appUpdateStatus.progress?.percent || 0}
                    colorScheme="blue"
                    size="lg"
                  />

                  <HStack justifyContent="space-between">
                    <Text fontSize="xs" color="coolGray.500">
                      {(appUpdateStatus.progress?.percent || 0).toFixed(1)}%
                    </Text>
                    <Text fontSize="xs" color="coolGray.500">
                      {formatBytes(appUpdateStatus.progress?.transferred || 0)} / {formatBytes(appUpdateStatus.progress?.total || 0)}
                    </Text>
                  </HStack>
                </VStack>
              </Box>
            )}

            {/* App Update Downloaded */}
            {appUpdateStatus.status === 'downloaded' && (
              <Box
                p={4}
                bg="green.50"
                borderRadius="md"
                borderWidth={1}
                borderColor="green.200"
              >
                <VStack space={2}>
                  <HStack space={2} alignItems="center">
                    <Icon
                      as={MaterialCommunityIcons}
                      name="check-circle"
                      size="md"
                      color="green.600"
                    />
                    <Text fontWeight="bold" color="green.700">
                      {getText('updateReadyToInstall')}
                    </Text>
                  </HStack>

                  <Text fontSize="sm" color="coolGray.600">
                    {getText('updateDownloadedDescription')}
                  </Text>

                  <Button
                    mt={2}
                    onPress={handleInstallAppUpdate}
                    colorScheme="green"
                    leftIcon={<Icon as={MaterialCommunityIcons} name="restart" />}
                  >
                    {getText('installAndRestart')}
                  </Button>
                </VStack>
              </Box>
            )}

            {/* App Update Installing */}
            {appUpdateStatus.status === 'installing' && (
              <Box
                p={4}
                bg="orange.50"
                borderRadius="md"
                borderWidth={1}
                borderColor="orange.200"
              >
                <VStack space={2}>
                  <HStack space={2} alignItems="center">
                    <Icon
                      as={MaterialCommunityIcons}
                      name="progress-clock"
                      size="md"
                      color="orange.600"
                    />
                    <Text fontWeight="bold" color="orange.700">
                      {getText('installingUpdate')}
                    </Text>
                  </HStack>

                  <Text fontSize="sm" color="coolGray.600">
                    {getText('installingUpdateDescription')}
                  </Text>
                </VStack>
              </Box>
            )}

            {/* App Update Error */}
            {appUpdateStatus.status === 'error' && (
              <Box
                p={4}
                bg="red.50"
                borderRadius="md"
                borderWidth={1}
                borderColor="red.200"
              >
                <VStack space={2}>
                  <HStack space={2} alignItems="center">
                    <Icon
                      as={MaterialCommunityIcons}
                      name="alert-circle"
                      size="md"
                      color="red.600"
                    />
                    <Text fontWeight="bold" color="red.700">
                      {getText('updateError')}
                    </Text>
                  </HStack>

                  <Text fontSize="sm" color="red.600">
                    {appUpdateStatus.error || 'An unknown error occurred'}
                  </Text>

                  <Button
                    mt={2}
                    onPress={handleCheckAppUpdate}
                    variant="outline"
                    colorScheme="red"
                    leftIcon={<Icon as={MaterialCommunityIcons} name="refresh" />}
                  >
                    {getText('tryAgain')}
                  </Button>
                </VStack>
              </Box>
            )}

            {/* No Update Available or Idle */}
            {(appUpdateStatus.status === 'idle' || appUpdateStatus.status === 'not-available') && (
              <Button
                onPress={handleCheckAppUpdate}
                variant="outline"
                leftIcon={<Icon as={MaterialCommunityIcons} name="update" />}
                isLoading={appUpdateStatus.status === 'checking'}
              >
                Check for App Update
              </Button>
            )}

            {appUpdateStatus.status === 'checking' && (
              <HStack space={2} alignItems="center" justifyContent="center">
                <Text fontSize="sm" color="coolGray.500">
                  {getText('checkingForUpdates')}
                </Text>
              </HStack>
            )}
          </>
        )}

        <Divider />

        {/* OTA Updates Section */}
        <Text fontSize="lg" fontWeight="semibold">
          {getText('componentUpdates')}
        </Text>

        {/* CPU Runtime Section (Windows Electron only) */}
        {isWindowsElectron && (
          <Box p={4} bg="coolGray.50" borderRadius="md" borderWidth={1} borderColor="coolGray.200">
            <VStack space={2}>
              <HStack space={2} alignItems="center">
                <Icon as={MaterialCommunityIcons} name="cpu-64-bit" size="md" color="coolGray.700" />
                <Text fontSize="md" fontWeight="bold">
                  AI Runtime (CPU)
                </Text>
              </HStack>

              {cpuInstalled ? (
                <Text fontSize="xs" color="coolGray.700">
                  Installed: {cpuInstalled.installed ? `Yes${cpuInstalled.version ? ` (v${cpuInstalled.version})` : ''}` : 'No'}
                </Text>
              ) : (
                <Text fontSize="xs" color="coolGray.600">
                  Checking CPU runtime…
                </Text>
              )}

              <HStack space={2}>
                <Button flex={1} size="sm" onPress={handleCpuRepair}>
                  {cpuInstalled?.installed ? 'Repair' : 'Install'}
                </Button>
              </HStack>
            </VStack>
          </Box>
        )}

        {/* CUDA Runtime Section (Windows Electron only) */}
        {isWindowsElectron && (
          <Box p={4} bg="coolGray.50" borderRadius="md" borderWidth={1} borderColor="coolGray.200">
            <VStack space={2}>
              <HStack space={2} alignItems="center">
                <Icon as={MaterialCommunityIcons} name="video-3d" size="md" color="coolGray.700" />
                <Text fontSize="md" fontWeight="bold">
                  GPU Acceleration (CUDA)
                </Text>
              </HStack>

              {cudaCompat ? (
                <Text fontSize="xs" color={cudaCompat.compatible ? 'green.700' : 'orange.700'}>
                  {cudaCompat.compatible ? 'Compatible NVIDIA GPU detected.' : 'CUDA not compatible with this machine.'}
                  {cudaCompat.gpus?.length ? ` GPU(s): ${cudaCompat.gpus.join(', ')}` : ''}
                </Text>
              ) : (
                <Text fontSize="xs" color="coolGray.600">
                  Checking GPU compatibility…
                </Text>
              )}

              {cudaInstalled ? (
                <Text fontSize="xs" color="coolGray.700">
                  Installed: {cudaInstalled.installed ? `Yes${cudaInstalled.version ? ` (v${cudaInstalled.version})` : ''}` : 'No'}
                </Text>
              ) : null}

              {cudaUpdate ? (
                <Text fontSize="xs" color="coolGray.700">
                  Update available: v{cudaUpdate.version}
                </Text>
              ) : (
                <Text fontSize="xs" color="coolGray.600">
                  No CUDA runtime update available.
                </Text>
              )}

              {cudaProgress?.status === 'downloading' ? (
                <VStack space={1}>
                  <Text fontSize="xs" color="coolGray.600">
                    Downloading… {cudaProgress?.progress ?? 0}%
                  </Text>
                  <Progress value={cudaProgress?.progress ?? 0} />
                </VStack>
              ) : null}

              {cudaProgress?.error ? (
                <Text fontSize="xs" color="red.600">
                  Error: {cudaProgress.error}
                </Text>
              ) : null}

              <HStack space={2}>
                <Button
                  flex={1}
                  size="sm"
                  onPress={handleCudaDownload}
                  isDisabled={!cudaUpdate || cudaProgress?.status === 'downloading' || cudaProgress?.status === 'ready' || cudaCompat?.compatible !== true}
                >
                  Download
                </Button>
                <Button
                  flex={1}
                  size="sm"
                  variant="outline"
                  onPress={handleCudaRetry}
                  isDisabled={!cudaUpdate || cudaProgress?.status === 'downloading' || cudaCompat?.compatible !== true}
                >
                  Retry
                </Button>
                <Button
                  flex={1}
                  size="sm"
                  colorScheme="green"
                  onPress={handleCudaApply}
                  isDisabled={cudaProgress?.status !== 'ready' || cudaCompat?.compatible !== true}
                >
                  Apply
                </Button>
              </HStack>
            </VStack>
          </Box>
        )}

        {/* OTA Update Status */}
        {hasUpdates && (
          <Box
            p={4}
            bg={mandatoryUpdatesCount > 0 ? 'orange.50' : 'blue.50'}
            borderRadius="md"
            borderWidth={1}
            borderColor={mandatoryUpdatesCount > 0 ? 'orange.200' : 'blue.200'}
          >
            <VStack space={2}>
              <HStack space={2} alignItems="center">
                <Icon
                  as={MaterialCommunityIcons}
                  name="cellphone-arrow-down"
                  size="md"
                  color={mandatoryUpdatesCount > 0 ? 'orange.600' : 'blue.600'}
                />
                <Text fontWeight="bold" color={mandatoryUpdatesCount > 0 ? 'orange.700' : 'blue.700'}>
                  {mandatoryUpdatesCount > 0 ? getText('mandatoryUpdatesAvailable') : getText('updatesAvailable')}
                </Text>
              </HStack>

              <Text fontSize="sm" color="coolGray.600">
                {availableUpdates.length} update{availableUpdates.length !== 1 ? 's' : ''} ready to download
              </Text>

              {hasReadyUpdates && (
                <HStack space={2} alignItems="center">
                  <Icon as={MaterialCommunityIcons} name="check-circle" size="sm" color="green.600" />
                  <Text fontSize="sm" color="green.700" fontWeight="bold">
                    Some updates are ready to install
                  </Text>
                </HStack>
              )}

              {isDownloading && (
                <HStack space={2} alignItems="center">
                  <Icon as={MaterialCommunityIcons} name="download" size="sm" color="blue.600" />
                  <Text fontSize="sm" color="blue.700">
                    Downloading updates...
                  </Text>
                </HStack>
              )}

              <Button
                mt={2}
                onPress={openUpdateModal}
                colorScheme={mandatoryUpdatesCount > 0 ? 'orange' : 'blue'}
                leftIcon={<Icon as={MaterialCommunityIcons} name="information" />}
              >
                {getText('viewDetails')}
              </Button>
            </VStack>
          </Box>
        )}

        {/* Check for OTA Updates Button */}
        <Button
          onPress={() => checkForUpdates()}
          variant="outline"
          leftIcon={<Icon as={MaterialCommunityIcons} name="refresh" />}
          isLoading={updateStatus === 'checking'}
        >
          {getText('checkForComponentUpdates')}
        </Button>

        {/* Debug Section */}
        <Box p={4} bg="gray.100" borderRadius="md" borderWidth={1} borderColor="gray.300">
          <VStack space={3}>
            <HStack space={2} alignItems="center">
              <Icon as={MaterialCommunityIcons} name="bug" size="sm" color="gray.600" />
              <Text fontSize="sm" fontWeight="bold" color="gray.700">
                Debug Options
              </Text>
            </HStack>
            <Text fontSize="xs" color="gray.600">
              Force check bypasses rate limiting. Check browser console for OTA logs.
            </Text>
            <Button
              onPress={forceCheckForUpdates}
              variant="outline"
              colorScheme="gray"
              size="sm"
              leftIcon={<Icon as={MaterialCommunityIcons} name="sync" />}
              isLoading={updateStatus === 'checking'}
            >
              Force Check (Bypass Rate Limit)
            </Button>
            <HStack space={2} alignItems="center">
              <Text fontSize="xs" color="gray.500">
                OTA Status: {updateStatus || 'idle'}
              </Text>
              {availableUpdates.length > 0 && (
                <Text fontSize="xs" color="gray.500">
                  | Updates: {availableUpdates.length}
                </Text>
              )}
            </HStack>
            {isElectron && (
              <Text fontSize="xs" color="gray.500">
                App Update Status: {appUpdateStatus.status}
              </Text>
            )}
          </VStack>
        </Box>

        {/* Info Section */}
        <Box p={4} bg="coolGray.50" borderRadius="md">
          <VStack space={2}>
            <Text fontSize="sm" fontWeight="bold">
              About Updates
            </Text>
            <Text fontSize="xs" color="coolGray.600">
              The app automatically checks for updates on startup. You can also manually check
              using the buttons above.
            </Text>
            <Text fontSize="xs" color="coolGray.600" mt={2}>
              Update types:
            </Text>
            <VStack space={1} ml={2}>
              {isElectron && (
                <HStack space={2} alignItems="center">
                  <Icon as={MaterialCommunityIcons} name="application" size="xs" />
                  <Text fontSize="xs" color="coolGray.600">
                    Full App Update - Complete application update with new features
                  </Text>
                </HStack>
              )}
              <HStack space={2} alignItems="center">
                <Icon as={MaterialCommunityIcons} name="memory" size="xs" />
                <Text fontSize="xs" color="coolGray.600">
                  Algo Code - Algorithm updates (Desktop)
                </Text>
              </HStack>
              <HStack space={2} alignItems="center">
                <Icon as={MaterialCommunityIcons} name="content-save" size="xs" />
                <Text fontSize="xs" color="coolGray.600">
                  Checkpoint - Model updates
                </Text>
              </HStack>
              <HStack space={2} alignItems="center">
                <Icon as={MaterialCommunityIcons} name="cpu-64-bit" size="xs" />
                <Text fontSize="xs" color="coolGray.600">
                  ML Runtime (CPU) - Required for local AI inference
                </Text>
              </HStack>
              <HStack space={2} alignItems="center">
                <Icon as={MaterialCommunityIcons} name="video-3d" size="xs" />
                <Text fontSize="xs" color="coolGray.600">
                  ML Runtime (CUDA) - Optional GPU acceleration for inference
                </Text>
              </HStack>
            </VStack>
          </VStack>
        </Box>
      </VStack>

      {/* Update Modal */}
      <UpdateModal isOpen={showUpdateModal} onClose={closeUpdateModal} />
    </Box>
  );
};
